import {
  deactivatePriceBook,
  deletePriceBook,
  readPriceBookDeleteConflict,
} from "../../../services";
import CatalogueDeleteDialog from "../shared/CatalogueDeleteDialog";
import { useCatalogueDelete } from "../shared/useCatalogueDelete";
import { buildPriceBookConflictMessage } from "./deleteConflict";
import { PriceBookDeleteTarget } from "./types";

const buildConsequences = (target: PriceBookDeleteTarget): string[] =>
  target.isDraft
    ? [
        `ราคา ${target.productCount} สินค้าในร่างถูกลบด้วย`,
        "สมุดราคาต้นฉบับและสาขาไม่เปลี่ยน",
        "กู้คืนไม่ได้",
      ]
    : [
        `ลบออกจากทุกสาขา (${target.branchCount} สาขา)`,
        `ราคา ${target.productCount} สินค้าในเล่มถูกลบด้วย`,
        'กู้คืนไม่ได้ — ถ้าแค่หยุดใช้ให้ใช้ "ปิด"',
      ];

const buildTitle = (target: PriceBookDeleteTarget | null): string => {
  if (!target) return "";
  const subject = target.isDraft ? "ลบร่าง" : "ลบสมุดราคา";
  return `${subject} ${target.code} ${target.name}`;
};

interface DeletePriceBookDialogProps {
  /** The pricebook being asked about; null keeps the dialog closed. */
  target: PriceBookDeleteTarget | null;
  onClose: () => void;
  onDeleted: () => void;
  /** The pricebook was switched off instead of deleted. */
  onDeactivated: () => void;
  /** The API refused the delete; the message says what has to happen first. */
  onRefused: (message: string) => void;
}

/**
 * Type-to-confirm delete for one pricebook, shared by the list row and the edit drawer so
 * both spell out the same consequences and handle a refusal the same way.
 */
export default function DeletePriceBookDialog({
  target,
  onClose,
  onDeleted,
  onDeactivated,
  onRefused,
}: DeletePriceBookDialogProps) {
  const deleteRecord = async () => {
    if (!target) return;
    try {
      await deletePriceBook(target.uuid);
      onDeleted();
    } catch (error) {
      // The interceptor already toasted the failure and the dialog closes either way; a 409
      // is a business rule whose reason the caller keeps on screen after the dialog closes.
      const conflict = readPriceBookDeleteConflict(error);
      if (conflict) {
        onRefused(buildPriceBookConflictMessage(conflict, target.code));
      }
    }
  };

  const deactivateRecord = async () => {
    if (!target) return;
    await deactivatePriceBook(target.uuid);
    onDeactivated();
  };

  const { deleting, deactivateAction, handleClose, handleDelete } =
    useCatalogueDelete({
      deleteRecord,
      deactivateRecord,
      deactivateLabel: "ปิดสมุดราคาแทน",
      // A null status is a synced row the API reads as enabled. A draft is thrown away, not
      // switched off.
      canDeactivate:
        target !== null && !target.isDraft && target.status !== "disable",
      onClose,
    });

  return (
    <CatalogueDeleteDialog
      open={Boolean(target)}
      title={buildTitle(target)}
      code={target?.code ?? ""}
      consequences={target ? buildConsequences(target) : []}
      deleting={deleting}
      deactivateAction={deactivateAction}
      onClose={handleClose}
      onDelete={handleDelete}
    />
  );
}
